import { AlphaHttpClient } from '@alpha-core/platform-ui';
import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { interval, Observable, switchMap } from 'rxjs';

// Interfaces for request and response
export interface ReassignmentDto {
    containerId: string;
    taskId: string | number;
    cid: string | number;
    currentUser: string;
    currentGroup: string;
    assignedTo: string;
}

export interface BulkReassignmentRequest {
    projectId: string;
    strategy: string;
    data: ReassignmentDto[];
    operationId?: string;
    comment?: string;
}

export interface BulkReassignmentResponse {
    id: string;
}

export interface OperationSummaryResponse {
    summary: TaskSummary[];
}

export interface TaskSummary {
    processId: string;
    containerId: string;
    taskId: string;
    assignedTo: string;
    assignmentStatus: string;
    status: 'completed' | 'in_progress' | 'failed';
}

@Injectable({
    providedIn: 'root',
})
export class BulkOperationService {
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_ADMIN_SERVICE_URL',
        },
    };
    alphaHttpClient = inject(AlphaHttpClient);

    /**
     * Perform a bulk reassignment operation.
     * @param requestBody The reassignment details.
     * @returns Observable of BulkReassignmentResponse
     */
    performBulkReassignment(requestBody: BulkReassignmentRequest): Observable<BulkReassignmentResponse> {
        const path = '/bulk-operation/operate';
        return this.alphaHttpClient.post<BulkReassignmentResponse>(this.domainUrl, path, requestBody);
    }

    /**
     * Get summary of a bulk operation by operation ID.
     * @param operationId The ID of the operation.
     * @returns Observable of OperationSummaryResponse
     */
    getOperationSummary(operationId: string): Observable<OperationSummaryResponse> | any {
        const path = `/bulk-operation/${operationId}/summary`;
        const options = {
            responseType: 'json',
            headers: new HttpHeaders().set('alpha-hide-spinner', true.toString()),
        };

        return this.alphaHttpClient.get<OperationSummaryResponse>(this.domainUrl, path, options);
    }

    /**
     * Track bulk operation progress using RxJS.
     * @param operationId The ID of the operation.
     * @returns Observable emitting SSE messages.
     */
    trackOperationProgress(operationId: string): Observable<any> {
        const url = `${window.environments.ALPHA_UI_ADMIN_SERVICE_URL}/bulk-operation/connect/${operationId}`;

        return new Observable((observer) => {
            const eventSource = new EventSource(url);

            // Handle incoming events
            eventSource.onmessage = (event) => {
                observer.next(event.data);
            };

            // Handle errors
            eventSource.onerror = (error) => {
                observer.error(error);
                eventSource.close(); // Close connection on error
            };

            // Handle subscription cleanup
            return () => {
                eventSource.close();
            };
        });
    }

    /**
     * Poll summary of a bulk operation every 1 second.
     * @param operationId The ID of the operation.
     * @returns Observable emitting summary at 1-second intervals.
     */
    pollOperationSummary(operationId: string): Observable<OperationSummaryResponse> | any {
        return interval(1000).pipe(switchMap(() => this.getOperationSummary(operationId)));
    }
}
